
const currencySelectors = {
  dropdownInput: ".js-dropdown-input",
  dropdownLabel: ".js-currency-dropdown-label",
  dropdownWrapper: ".js-dropdown-wrapper",
  dropdownOptions: ".js-dropdown-options",
  dropdownOption: ".js-dropdown-option",
  dropdownChevron: ".js-dropdown-chevron"
};

class CurrencySelector extends HTMLElement {
  constructor() {
    super();
    this.eventListeners = [];
    this.initialized = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.initializeCurrencySelector();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });

    this.eventListeners = [];
    this.initialized = false;
  }

  addEventListenerWithCleanup(element, event, handler) {
    element.addEventListener(event, handler);
    this.eventListeners.push({ element, event, handler });
  }

  initializeCurrencySelector() {
    this.dropdownInput = this.querySelector(
      currencySelectors.dropdownInput
    );

    this.dropdownLabel = this.querySelector(
      currencySelectors.dropdownLabel
    );

    this.dropdownWrapper = this.querySelector(
      currencySelectors.dropdownWrapper
    );

    this.dropdownOptions = this.querySelector(
      currencySelectors.dropdownOptions
    );

    this.dropdownChevron = this.querySelector(
      currencySelectors.dropdownChevron
    );

    const options = this.querySelectorAll(
      currencySelectors.dropdownOption
    );

    if (
      !this.dropdownInput ||
      !this.dropdownLabel ||
      !this.dropdownWrapper ||
      !this.dropdownOptions ||
      !this.dropdownChevron ||
      !options.length
    ) {
      console.warn("Currency selector: Missing required elements");
      return;
    }

    this.initialized = true;

    // Open or close dropdown when clicking label
    this.addEventListenerWithCleanup(
      this.dropdownLabel,
      "click",
      () => this.toggleOptions()
    );

    // Open or close dropdown when clicking chevron
    this.addEventListenerWithCleanup(
      this.dropdownChevron,
      "click",
      () => this.toggleOptions()
    );

    // Handle country/currency selection
    options.forEach((option) => {
      const handler = () => {
        const selectedCountry = option.value;

        if (this.dropdownInput.value !== selectedCountry) {
          this.dropdownInput.value = selectedCountry;

          this.updateLabel();
          this.updateSelectedOption(selectedCountry);
          this.handleCurrencyChange(selectedCountry);
        }

        this.closeOptions();
      };

      this.addEventListenerWithCleanup(
        option,
        "click",
        handler
      );
    });

    // Close dropdown when clicking outside
    this.addEventListenerWithCleanup(
      document,
      "click",
      (event) => {
        if (!this.contains(event.target)) {
          this.closeOptions();
        }
      }
    );

    // Keyboard accessibility
    const keyboardHandler = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleOptions();
      }

      if (event.key === "Escape") {
        this.closeOptions();
      }
    };

    this.addEventListenerWithCleanup(
      this.dropdownLabel,
      "keydown",
      keyboardHandler
    );

    this.addEventListenerWithCleanup(
      this.dropdownChevron,
      "keydown",
      keyboardHandler
    );

    // Accessibility attributes
    this.dropdownLabel.setAttribute("aria-haspopup", "true");
    this.dropdownLabel.setAttribute("aria-expanded", "false");

    this.dropdownOptions.setAttribute("role", "listbox");

    options.forEach((option) => {
      option.setAttribute("role", "option");

      const isSelected =
        option.value === this.dropdownInput.value;

      option.setAttribute(
        "aria-selected",
        String(isSelected)
      );

      option.classList.toggle("selected", isSelected);
    });
  }

  updateLabel() {
    const selectedOption = Array.from(
      this.querySelectorAll(currencySelectors.dropdownOption)
    ).find(
      (option) => option.value === this.dropdownInput.value
    );

    if (selectedOption) {
      // Show currency without changing dropdown styling
      const currencyText = selectedOption.textContent
        .split(" - ")[0]
        .trim();

      this.dropdownLabel.textContent = currencyText;
    }
  }

  updateSelectedOption(selectedCountry) {
    this.querySelectorAll(
      currencySelectors.dropdownOption
    ).forEach((option) => {
      const isSelected = option.value === selectedCountry;

      option.setAttribute(
        "aria-selected",
        String(isSelected)
      );

      option.classList.toggle("selected", isSelected);
    });
  }

  toggleOptions() {
    const isActive =
      this.dropdownLabel.classList.contains("active");

    if (isActive) {
      this.closeOptions();
    } else {
      this.dropdownLabel.classList.add("active");
      this.dropdownChevron.classList.add("active");
      this.dropdownWrapper.classList.add("active");
      this.dropdownOptions.classList.add("active");

      this.dropdownLabel.setAttribute(
        "aria-expanded",
        "true"
      );
    }
  }

  closeOptions() {
    this.dropdownLabel.classList.remove("active");
    this.dropdownChevron.classList.remove("active");
    this.dropdownWrapper.classList.remove("active");
    this.dropdownOptions.classList.remove("active");

    this.dropdownLabel.setAttribute(
      "aria-expanded",
      "false"
    );
  }

  // Shopify Markets localization
  // Replaces the old /cart/update submission
  handleCurrencyChange(countryCode) {
    const form = document.getElementById(
      "FooterCurrencyForm"
    );

    if (!form) {
      console.error(
        "Currency selector: Shopify localization form not found"
      );
      return;
    }

    const countryInput = form.querySelector(
      'input[name="country_code"]'
    );

    if (!countryInput) {
      console.error(
        "Currency selector: Country input not found"
      );
      return;
    }

    // Set selected Shopify Markets country
    countryInput.value = countryCode;

    this.suppressGeolocationModal();

    // Prevent duplicate submissions
    this.dropdownLabel.textContent = "Updating...";
    this.dropdownLabel.disabled = true;
    this.dropdownChevron.disabled = true;

    // Submit to Shopify localization endpoint
    form.submit();
  }

  // The Orbe Geolocation app re-prompts whenever the chosen country differs
  // from the visitor's IP country, defaulting back to the IP country. A manual
  // choice here should win, so set the same cookie Orbe writes when its modal
  // is closed (host-only, 7 days = Orbe's modal_close_time default).
  suppressGeolocationModal() {
    const maxAgeSeconds = 7 * 24 * 60 * 60;
    document.cookie = `mdApp_cookieModalTime=true;path=/;max-age=${maxAgeSeconds};`;
  }
}

// Register custom element
// Renamed from <currency-selector>: the hashed Vite chunk for that tag is
// cached by Shopify's CDN for a year, so in-place edits never reach shoppers.
if (!customElements.get("currency-selector-v2")) {
  customElements.define(
    "currency-selector-v2",
    CurrencySelector
  );
}